import { useState } from 'react';
import PropTypes from 'prop-types';
import EngagementIcon from 'apps/profile/components/engagement-icon';
import ModalConfirm from 'components/modals/modal-confirm';
import GiftsFeed from 'apps/profile/components/activity/gifts-feed';
import { formatDate, formatGiftAmount, formatGiftType } from 'apps/profile/components/activity/activity-helpers';
import RelatedGiftsResolver from 'apps/profile/components/related-gifts-resolver';
import ContactResolver from 'apps/profile/components/activity/contact-resolver';
import Decorator from 'clientDecorator';
import { Link, LinkButton } from '@evertrue/et-components';

function renderGiftProperty(label, value) {
  return (
    <div key={label} className="gift-details-modal--fact">
      <div className="gift-details-modal--fact-label">{label}</div>
      <div className="gift-details-modal--fact-value">{value}</div>
    </div>
  );
}

function formatListValue(list = []) {
  if (list.length > 0) {
    return list.map((item) => item.split(';').join('; ')).join(', ');
  }
  return 'Not provided';
}

function renderNameLink(g) {
  if (g.contact_id) return `/contact/${g.contact_id}/gifts`;
  return null;
}

const GiftDetailsModal = ({ gift = {}, onHide = () => {} }) => {
  const [showId, setShowId] = useState(false);
  const [navigatedGift, setNavigatedGift] = useState(null);

  const activeGift = navigatedGift || gift;

  const getGiftClickHandler = (clickedGift) => {
    if (clickedGift.remote_id === activeGift.remote_id) return undefined;
    return () => {
      setNavigatedGift(clickedGift);
      setShowId(false);
    };
  };

  const handleBack = () => {
    setNavigatedGift(null);
    setShowId(false);
  };

  const {
    occurred_at,
    type,
    amount,
    remote_id,
    contact_id,
    donor_name,
    designations,
    designation_categories,
    campaigns,
    transaction_type,
    legal_credit,
    related_gift_remote_id,
  } = activeGift;

  const header = navigatedGift ? (
    <div className="gift-details-modal--header-content">
      <LinkButton
        title="Back to previous gift"
        aria-label="Back to previous gift"
        className="gift-details-modal--back-btn"
        onClick={handleBack}
      >
        {'\u2190 Back'}
      </LinkButton>
      <span>Gift Detail</span>
    </div>
  ) : (
    'Gift Detail'
  );

  return (
    <ModalConfirm
      className="gift-details-modal"
      onCancel={onHide}
      header={header}
      render={() => (
        <div className="gift-details-modal--contents">
          <div className="gift-details-modal--summary">
            <EngagementIcon className="gift-details-modal--icon" size="40px" icon="gift" />
            <div className="gift-details-modal--summary-content">
              <div className="gift-details-modal--type">{formatGiftType(type)}</div>
              <div className="gift-details-modal--date" onDoubleClick={() => setShowId((prev) => !prev)}>
                {showId ? `GIFT ID: ${remote_id}` : formatDate(occurred_at)}
              </div>
              {navigatedGift && contact_id && (
                <div className="gift-details-modal--constituent">
                  <ContactResolver
                    contact_id={contact_id}
                    render={({ parsed_contact }) => {
                      const name = parsed_contact?.id
                        ? Decorator.Contacts.getFullName(parsed_contact)?.trim?.()
                        : donor_name;
                      return (
                        <Link title="View constituent gift transactions" href={`/contact/${contact_id}/gifts`}>
                          {name || 'View Constituent'}
                        </Link>
                      );
                    }}
                  />
                </div>
              )}
            </div>
            <div className="gift-details-modal--amount">{formatGiftAmount(amount)}</div>
          </div>

          <div className="gift-details-modal--details">
            {renderGiftProperty('Designations', formatListValue(designations))}
            {renderGiftProperty('Designation Categories', formatListValue(designation_categories))}
            {renderGiftProperty('Campaign Credit', formatListValue(campaigns))}
            {renderGiftProperty('Transaction Type', transaction_type || 'Not provided')}
            {renderGiftProperty('Legal Credit', legal_credit ? 'Y' : 'N')}

            {related_gift_remote_id && type && (
              <RelatedGiftsResolver
                related_gift_remote_id={related_gift_remote_id}
                type={type}
                render={({ gifts, loading }) => (
                  <GiftsFeed
                    items={gifts}
                    feedTitle="RELATED GIFTS"
                    loading={loading}
                    renderNameLink={renderNameLink}
                    getGiftAmountClickHandler={getGiftClickHandler}
                  />
                )}
              />
            )}
          </div>
        </div>
      )}
    />
  );
};

GiftDetailsModal.propTypes = {
  gift: PropTypes.object,
  onHide: PropTypes.func,
};

export default GiftDetailsModal;
